import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import vendorCategoryService from "./vendorCategoryService";

const EMPTY_FORM = {
  name: "",
  code: "",
  description: "",
  status: "Active",
};

const normalizeCategory = (item) => ({
  ...item,
  id: item.id,
  name: item.name ?? "",
  code: item.code ?? "",
  description: item.description ?? "",
  status: item.status ?? "Active",
});

const getErrorMessage = (result, fallback) =>
  result?.message || fallback;

export default function VendorCategories() {
  const [collapsed, setCollapsed] = useState(false);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteCategoryItem, setDeleteCategoryItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    setPageError("");

    try {
      const params = {
        per_page: 200,
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (statusFilter !== "All") {
        params.status = statusFilter;
      }

      const result = await vendorCategoryService.getAll(params);

      if (!result?.success) {
        setCategories([]);
        setPageError(
          getErrorMessage(result, "Unable to load vendor categories.")
        );
        return;
      }

      // Supports Laravel paginator responses and regular data arrays.
      const payload = result.data;
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      setCategories(rows.map(normalizeCategory));
    } catch (err) {
      setCategories([]);
      setPageError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load vendor categories."
      );
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const activeCount = useMemo(
    () => categories.filter((item) => item.status === "Active").length,
    [categories]
  );

  const inactiveCount = useMemo(
    () => categories.filter((item) => item.status === "Inactive").length,
    [categories]
  );

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const openEdit = (category) => {
    setEditingId(category.id);
    setForm({
      name: category.name,
      code: category.code,
      description: category.description,
      status: category.status,
    });
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setError("");
  };

  const saveCategory = async (event) => {
    event.preventDefault();

    const name = form.name.trim();
    const code = form.code.trim().toUpperCase();
    const description = form.description.trim();

    if (!name || !code) {
      setError("Category name and category code are required.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    const payload = {
      name,
      code,
      description: description || null,
      status: form.status,
    };

    try {
      const result =
        editingId !== null
          ? await vendorCategoryService.update(editingId, payload)
          : await vendorCategoryService.create(payload);

      if (!result?.success) {
        const validationErrors = result?.errors || {};
        const firstValidationError = Object.values(validationErrors)
          .flat()
          .find(Boolean);

        setError(
          firstValidationError ||
            getErrorMessage(
              result,
              editingId !== null
                ? "Unable to update category."
                : "Unable to create category."
            )
        );
        return;
      }

      setModalOpen(false);
      setEditingId(null);
      setForm({ ...EMPTY_FORM });
      setSuccess(
        editingId !== null
          ? "Vendor category updated successfully."
          : "Vendor category created successfully."
      );

      await loadCategories();
    } catch (err) {
      const validationErrors = err?.response?.data?.errors || {};
      const firstValidationError = Object.values(validationErrors)
        .flat()
        .find(Boolean);

      setError(
        firstValidationError ||
          err?.response?.data?.message ||
          err?.message ||
          "Unable to save vendor category."
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async () => {
    if (!deleteCategoryItem) return;

    setDeleting(true);
    setError("");
    setSuccess("");

    try {
      const result = await vendorCategoryService.delete(
        deleteCategoryItem.id
      );

      if (!result?.success) {
        setPageError(
          getErrorMessage(result, "Unable to delete vendor category.")
        );
        return;
      }

      setDeleteCategoryItem(null);
      setSuccess("Vendor category deleted successfully.");
      await loadCategories();
    } catch (err) {
      setPageError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to delete vendor category."
      );
    } finally {
      setDeleting(false);
    }
  };

  const exportCategories = () => {
    const columns = ["name", "code", "description", "status"];

    const escapeCSV = (value) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;

    const csv = [
      columns.join(","),
      ...categories.map((item) =>
        columns.map((column) => escapeCSV(item[column])).join(",")
      ),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "vendor-categories.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="flex">
        <Sidebar
          isCollapsed={collapsed}
          onToggleCollapse={() => setCollapsed((value) => !value)}
        />

        <main
          className={`mt-16 min-w-0 flex-1 transition-all ${
            collapsed ? "ml-16" : "ml-72"
          }`}
        >
          <div className="p-5 md:p-8">
            <Breadcrumb />

            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Icon
                    name="Building2"
                    size={24}
                    className="text-primary"
                  />
                </div>

                <div>
                  <h1 className="text-2xl font-bold text-foreground md:text-3xl">
                    Vendor Categories
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Manage vendor categories stored in the database.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={exportCategories}
                  disabled={loading || categories.length === 0}
                >
                  <Icon name="Download" size={16} />
                  Export CSV
                </Button>

                <Button onClick={openAdd}>
                  <Icon name="Plus" size={16} />
                  Add Category
                </Button>
              </div>
            </div>

            {success && (
              <div className="mb-5 flex items-center justify-between rounded-lg border border-success/20 bg-success/10 p-4 text-sm text-success">
                <span>{success}</span>
                <button
                  type="button"
                  onClick={() => setSuccess("")}
                  aria-label="Dismiss success message"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            )}

            {pageError && (
              <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-error/20 bg-error/10 p-4 text-sm text-error">
                <span>{pageError}</span>
                <button
                  type="button"
                  onClick={() => setPageError("")}
                  aria-label="Dismiss error message"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            )}

            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                {
                  label: "Categories Loaded",
                  value: categories.length,
                  icon: "Folders",
                },
                {
                  label: "Active Categories",
                  value: activeCount,
                  icon: "CheckCircle",
                },
                {
                  label: "Inactive Categories",
                  value: inactiveCount,
                  icon: "CircleOff",
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl border border-border bg-card p-5"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {stat.label}
                      </p>
                      <p className="mt-2 text-3xl font-bold text-foreground">
                        {stat.value}
                      </p>
                    </div>
                    <Icon
                      name={stat.icon}
                      size={23}
                      className="text-primary"
                    />
                  </div>
                </div>
              ))}
            </div>

            <section className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-5">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    Vendor Category Directory
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {loading
                      ? "Loading categories..."
                      : `${categories.length} categories loaded from the API`}
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search categories..."
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm sm:w-64"
                  />

                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  >
                    <option value="All">All statuses</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>

                  <Button
                    variant="outline"
                    onClick={loadCategories}
                    disabled={loading}
                  >
                    <Icon name="RefreshCw" size={16} />
                    Refresh
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-muted/30">
                    <tr>
                      {[
                        "Category",
                        "Code",
                        "Description",
                        "Status",
                        "Actions",
                      ].map((heading) => (
                        <th
                          key={heading}
                          className="whitespace-nowrap px-5 py-4 text-xs font-semibold uppercase text-muted-foreground"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-5 py-12 text-center text-sm text-muted-foreground"
                        >
                          <span className="inline-flex items-center gap-2">
                            <Icon name="LoaderCircle" size={18} />
                            Loading vendor categories...
                          </span>
                        </td>
                      </tr>
                    ) : categories.length > 0 ? (
                      categories.map((category) => (
                        <tr
                          key={category.id}
                          className="border-t border-border hover:bg-muted/20"
                        >
                          <td className="whitespace-nowrap px-5 py-4 font-medium text-foreground">
                            {category.name}
                          </td>

                          <td className="whitespace-nowrap px-5 py-4 text-sm">
                            {category.code}
                          </td>

                          <td className="min-w-64 px-5 py-4 text-sm text-muted-foreground">
                            {category.description || "—"}
                          </td>

                          <td className="px-5 py-4">
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-medium ${
                                category.status === "Active"
                                  ? "bg-success/10 text-success"
                                  : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {category.status}
                            </span>
                          </td>

                          <td className="whitespace-nowrap px-5 py-4">
                            <button
                              type="button"
                              title="Edit category"
                              onClick={() => openEdit(category)}
                              className="mr-3 text-primary"
                            >
                              <Icon name="Pencil" size={17} />
                            </button>

                            <button
                              type="button"
                              title="Delete category"
                              onClick={() => setDeleteCategoryItem(category)}
                              className="text-destructive"
                            >
                              <Icon name="Trash2" size={17} />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-5 py-12 text-center text-sm text-muted-foreground"
                        >
                          <Icon
                            name="FolderSearch"
                            size={26}
                            className="mx-auto mb-3"
                          />
                          <p className="font-medium text-foreground">
                            No vendor categories found
                          </p>
                          <p className="mt-1">
                            {search || statusFilter !== "All"
                              ? "Try changing your search or status filter."
                              : "No categories have been returned by the API yet."}
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/50 p-4">
          <form
            onSubmit={saveCategory}
            className="my-auto w-full max-w-lg rounded-xl border border-border bg-card p-6 shadow-xl"
          >
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-foreground">
                {editingId !== null
                  ? "Edit Vendor Category"
                  : "Add Vendor Category"}
              </h2>

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="rounded-lg p-2 hover:bg-muted"
                aria-label="Close modal"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {error && (
              <div className="mt-4 rounded-lg border border-error/20 bg-error/10 p-3 text-sm text-error">
                {error}
              </div>
            )}

            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Category Name *
                </label>
                <input
                  required
                  maxLength={100}
                  value={form.name}
                  onChange={(event) =>
                    setForm({ ...form, name: event.target.value })
                  }
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5"
                  placeholder="Enter category name"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">
                  Category Code *
                </label>
                <input
                  required
                  maxLength={30}
                  value={form.code}
                  onChange={(event) =>
                    setForm({ ...form, code: event.target.value })
                  }
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5"
                  placeholder="Enter unique category code"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">
                  Description
                </label>
                <textarea
                  rows={3}
                  maxLength={5000}
                  value={form.description}
                  onChange={(event) =>
                    setForm({ ...form, description: event.target.value })
                  }
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5"
                  placeholder="Describe this vendor category"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">
                  Status
                </label>
                <select
                  value={form.status}
                  onChange={(event) =>
                    setForm({ ...form, status: event.target.value })
                  }
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </Button>

              <Button type="submit" disabled={saving}>
                <Icon
                  name={saving ? "LoaderCircle" : "Save"}
                  size={16}
                />
                {saving
                  ? "Saving..."
                  : editingId !== null
                    ? "Save Changes"
                    : "Create Category"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {deleteCategoryItem && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-foreground">
              Delete Category?
            </h2>

            <p className="mt-2 text-sm text-muted-foreground">
              Are you sure you want to delete{" "}
              <strong>{deleteCategoryItem.name}</strong>? This action will
              delete the record from the backend database.
            </p>

            {pageError && (
              <p className="mt-3 text-sm text-error">{pageError}</p>
            )}

            <div className="mt-5 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteCategoryItem(null)}
                disabled={deleting}
              >
                Cancel
              </Button>

              <Button
                type="button"
                onClick={deleteCategory}
                disabled={deleting}
              >
                <Icon
                  name={deleting ? "LoaderCircle" : "Trash2"}
                  size={16}
                />
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
